import {
  Button,
  Checkbox,
  Form,
  FormProps,
  Input,
  Modal,
  ModalProps,
  Typography,
} from "antd";
import { CheckboxChangeEvent } from "antd/es/checkbox";
import { useCallback, useEffect, useMemo, useState } from "react";
import SingleLineTextArea from "../../../components/pages/payment-request/singleLineTextArea";
import { IBank } from "../../../interfaces/banks";
import { ICompany } from "../../../interfaces/companies";
import { ButtonListFooterModalSM } from "../../../pages/components/buttonsList";
import { confirmModalUnsavedChangesProps } from "../../../pages/components/constants";
import ICurrency from "../../../pages/currencies/currency.interface";
import {
  abaRoutingNumberRules,
  accountNumberRules,
  bankStateBranchNumberRules,
  branchNumberRules,
  ibanRules,
  isBankAccountFieldOptional,
  isIbanRequiredByCountryAndCurrency,
} from "../../../pages/payment-request/addAccount.constants";
import {
  companyAddress1Rules,
  companyAddress2Rules,
  companyCityRules,
  companyCountryRules,
  companyDirectorsFirstNameRules,
  companyDirectorsLastNameRules,
  companyEmailRules,
  companyPostalCodeRules,
  companyRegistrationNumberRules,
  companyStateProvinceRules,
  companyTelRules,
  companyTinVatUidRules,
  ProviderFullNameRules,
  ProviderNameRules
} from "../../../pages/payment-request/addCompany.constants";
import { mapCurrencyItem } from "../../../utils/edit.constants";
import {
  bankRulesSimple,
  clearFieldErrorMessageOnChange,
  currencyRulesSimple,
  trimFormFieldValue,
  websiteRules,
} from "../../../utils/form.constants";
import { useNotificationOkCancel } from "../../../utils/hooks/useNotificationOkCancel.hook";
import { AutoComplete, AutoCompleteBankInput } from "../../inputs/autocomplete";
import { Option } from "../../inputs/autocomplete/autocomplete";
import { DownOutlined, PlusOutlined } from "@ant-design/icons";
import { useModalForm } from "@refinedev/antd";
import { NewBankModalCreate } from "./addBank";
import { useUserDetails } from "../../pages/auth/components/auth/hooks";

interface Props {
  formProps: FormProps;
  modalProps: ModalProps;
  formIsLoading: boolean;
  close: () => void;
  onFinish: (data: any) => void;
}

export const NewCompanyModalCreate = ({
  close,
  modalProps,
  formProps,
  formIsLoading,
  onFinish,
}: Props) => {
  const [checkboxState, setCheckboxState] = useState(true);
  const [currencyData, setCurrencyData] = useState<ICurrency>();
  const [bankData, setBankData] = useState<IBank>();
  const [bankInputRefreshKey, setBankInputRefreshKey] = useState(0);
  const details = useUserDetails();

  const {
    modalProps: createBankModalProps,
    formProps: createBankFormProps,
    show: createBankModalShow,
    formLoading: createBankFormLoading,
    submit: createBankFormSubmit,
    close: createBankModalClose,
  } = useModalForm({
    warnWhenUnsavedChanges: false,
    action: "create",
  });

  const onSubmit = useCallback(
    async (formValues: ICompany) => {
      const data = {
        name: formValues.name,
        fullName: formValues?.fullName,
        directorFirstName: formValues.directorFirstName,
        directorLastName: formValues.directorLastName,
        country: formValues.country,
        stateProvince: formValues.stateProvince,
        postalCode: formValues.postalCode,
        city: formValues.city,
        address1: formValues.address1,
        address2: formValues?.address2,
        tinVatUid: formValues?.tinVatUid,
        regNo: formValues?.regNo,
        phoneNumber1: formValues?.phoneNumber1,
        phoneNumber2: formValues?.phoneNumber2,
        email: formValues?.email,
        website: formValues?.website,
        default: checkboxState,
        bankAccount: {
          bankId: bankData?.id,
          currencyCode: currencyData?.code,
          accountNumber: formValues?.accountNumber,
          abaRoutingNumber: formValues?.abaRoutingNumber,
          bankStateBranchNumber: formValues?.bankStateBranchNumber,
          branchNumber: formValues?.branchNumber,
          iban: formValues?.iban,
        },
      };
      try {
        await onFinish(data);
        setCheckboxState(true);
        close();
      } catch (error) {
        // Error is handled by the form/API, don't close the modal
      }
    },
    [checkboxState, bankData?.id, currencyData?.code, onFinish, close]
  );

  const onSave = useCallback(() => {
    formProps.form?.submit();
  }, [formProps]);

  const { openNotification, contextHolder } = useNotificationOkCancel({
    ...confirmModalUnsavedChangesProps,
    onOk: close,
  });

  const onModalCancel = useCallback(() => {
    const formIsNotEmpty = Object.values(formProps.form?.getFieldsValue()).some(
      (elm) => elm !== undefined
    );
    if (formIsNotEmpty) {
      openNotification();
    } else {
      close();
    }
  }, [close, formProps.form, openNotification]);

  const onChangeCheckbox = (event: CheckboxChangeEvent) => {
    setCheckboxState(event.target.checked);
  };

  const clearCurrencyData = useCallback(() => {
    setCurrencyData(undefined);
  }, []);

  const clearBankData = useCallback(() => {
    setBankData(undefined);
  }, []);

  const changeBankData = useCallback(
    (data: IBank) => {
      if (data.id !== bankData?.id) {
        setBankData(data);
      }
    },
    [bankData?.id]
  );

  const onSelectBank = useCallback(
    (_: IBank, { data }: Option<IBank>) => changeBankData(data),
    [changeBankData]
  );

  const handleBankCreated = useCallback(
    (createdBank?: IBank) => {
      setBankInputRefreshKey((prev) => prev + 1);

      if (!createdBank?.id) {
        return;
      }

      changeBankData(createdBank);
      // AutoCompleteBankInput uses bank name as option value.
      formProps.form?.setFieldValue("bankId", createdBank.name);
    },
    [formProps.form, changeBankData]
  );

  const afterClose = () => {
    formProps.form?.resetFields();
    setCheckboxState(true);
  };

  useEffect(() => {
    if (!bankData) {
      formProps.form?.resetFields([
        "abaRoutingNumber",
        "bankStateBranchNumber",
        "branchNumber",
        "iban",
      ]);
    } else {
      if (bankData.country === "US") {
        formProps.form?.resetFields([
          "bankStateBranchNumber",
          "branchNumber",
          "iban",
        ]);
      } else if (bankData.country === "AU") {
        formProps.form?.resetFields([
          "abaRoutingNumber",
          "branchNumber",
          "iban",
        ]);
      } else if (
        isBankAccountFieldOptional(bankData.country, currencyData?.code)
      ) {
        formProps.form?.resetFields([
          "abaRoutingNumber",
          "bankStateBranchNumber",
          "branchNumber",
          "iban",
        ]);
      } else {
        formProps.form?.resetFields([
          "abaRoutingNumber",
          "bankStateBranchNumber",
        ]);
      }
    }
  }, [bankData, currencyData, formProps.form]);

  const numberField = useMemo(() => {
    if (bankData?.country === "US") {
      return (
        <>
          <Form.Item
            name="abaRoutingNumber"
            rules={abaRoutingNumberRules(bankData?.country)}
          >
            <Input placeholder="ABA routing number" />
          </Form.Item>
          <div style={{ marginTop: '-16px', display: 'flex', justifyContent: 'start', marginBottom: '16px' }}>
            <Typography.Text
              style={{
                color: '#F28A28',
                fontSize: 12,
                fontWeight: 400,
                lineHeight: '16px',
              }}>
              {
                'If not available, please key in 000000.'
              }
            </Typography.Text>
          </div>
        </>
      );
    }

    if (bankData?.country === "AU") {
      return (
        <>
          <Form.Item
            name="bankStateBranchNumber"
            rules={bankStateBranchNumberRules(bankData?.country)}
          >
            <Input placeholder="Bank State Branch number" />
          </Form.Item>
          <div style={{ marginTop: '-16px', display: 'flex', justifyContent: 'start', marginBottom: '16px' }}>
            <Typography.Text
              style={{
                color: '#F28A28',
                fontSize: 12,
                fontWeight: 400,
                lineHeight: '16px',
              }}>
              {
                'If not available, please key in 000000.'
              }
            </Typography.Text>
          </div>
        </>
      );
    }

    if (
      !isBankAccountFieldOptional(bankData?.country, currencyData?.code) &&
      !isIbanRequiredByCountryAndCurrency(bankData?.country, currencyData?.code)
    ) {
      return (
        <>
          <Form.Item
            name="branchNumber"
            rules={branchNumberRules(bankData?.country, currencyData?.code)}
          >
            <Input placeholder="Branch number" />
          </Form.Item>
          <div style={{ marginTop: '-16px', display: 'flex', justifyContent: 'start', marginBottom: '16px' }}>
            <Typography.Text
              style={{
                color: '#F28A28',
                fontSize: 12,
                fontWeight: 400,
                lineHeight: '16px',
              }}>
              {
                'If not available, please key in 000000.'
              }
            </Typography.Text>
          </div>
        </>
      );
    }
  }, [bankData, currencyData]);

  const ibanField = useMemo(() => {
    if (
      isIbanRequiredByCountryAndCurrency(bankData?.country, currencyData?.code)
    ) {
      return (
        <Form.Item
          name="iban"
          rules={ibanRules(bankData?.country, currencyData?.code)}
        >
          <Input placeholder="IBAN" />
        </Form.Item>
      );
    }
    return null;
  }, [bankData, currencyData]);

  return (
    <Modal
      {...modalProps}
      title="Add new provider"
      className="smartModalSM"
      afterClose={afterClose}
      onCancel={onModalCancel}
      footer={ButtonListFooterModalSM(onSave, onModalCancel, formIsLoading)}
    >
      {contextHolder}
      <Form
        {...formProps}
        layout="vertical"
        onFinish={onSubmit}
        disabled={formIsLoading}
        onValuesChange={clearFieldErrorMessageOnChange(formProps.form)}
      >
        <Form.Item name="name" rules={ProviderNameRules}>
          <SingleLineTextArea placeholder="Provider Name" />
        </Form.Item>
        <Form.Item name="fullName" rules={ProviderFullNameRules}>
          <SingleLineTextArea placeholder="Full Provider Name" />
        </Form.Item>
        <Form.Item
          name="directorFirstName"
          rules={companyDirectorsFirstNameRules}
        >
          <SingleLineTextArea placeholder={`Director's first name`} />
        </Form.Item>
        <Form.Item
          name="directorLastName"
          rules={companyDirectorsLastNameRules}
        >
          <SingleLineTextArea placeholder={`Director's last name`} />
        </Form.Item>
        <Form.Item name="address1" rules={companyAddress1Rules}>
          <SingleLineTextArea placeholder="Address 1" />
        </Form.Item>
        <Form.Item name="address2" rules={companyAddress2Rules}>
          <SingleLineTextArea placeholder="Address 2" />
        </Form.Item>
        <Form.Item name="city" rules={companyCityRules}>
          <SingleLineTextArea placeholder={`City`} />
        </Form.Item>
        <Form.Item name="stateProvince" rules={companyStateProvinceRules}>
          <SingleLineTextArea placeholder={`State / Province`} />
        </Form.Item>
        <Form.Item name="postalCode" rules={companyPostalCodeRules}>
          <SingleLineTextArea placeholder={`Postal Code`} />
        </Form.Item>
        <Form.Item name="country" rules={companyCountryRules}>
          <AutoComplete
            resource="api/v1/worldCheck/countries"
            mapItem={(item: { value: string; label: string }) => ({
              value: item.label,
              key: item.value,
              label: <Typography.Text>{item.label}</Typography.Text>,
              data: item,
            })}
            notFoundPlaceholder="Start by typing country name"
          >
            <Input
              // style={{ fontSize: 16 }}
              placeholder="Country of incorporation"
              suffix={<DownOutlined />}
            />
          </AutoComplete>
        </Form.Item>
        <Form.Item name="tinVatUid" rules={companyTinVatUidRules}>
          <Input placeholder="VAT/TIN/UID" maxLength={20} />
        </Form.Item>
        <Form.Item name="regNo" rules={companyRegistrationNumberRules}>
          <Input placeholder="Reg &#8470;" maxLength={20} />
        </Form.Item>
        <Form.Item name="phoneNumber1" rules={companyTelRules}>
          <Input placeholder="Tel 1" />
        </Form.Item>
        <Form.Item name="phoneNumber2" rules={companyTelRules}>
          <Input placeholder="Tel 2" />
        </Form.Item>
        <Form.Item name="email" rules={companyEmailRules}>
          <Input placeholder="Email" maxLength={256} />
        </Form.Item>
        <Form.Item name="website" rules={websiteRules}>
          <Input
            placeholder="Website"
            maxLength={256}
            onBlur={trimFormFieldValue("website", formProps.form)}
          />
        </Form.Item>
        <div style={{ display: "flex", gap: 8 }}>
          <div style={{ flex: 1, marginTop: '-10px' }}>
            <Form.Item name="bankId" rules={bankRulesSimple(bankData)}>
              <AutoCompleteBankInput
                key={bankInputRefreshKey}
                onSelect={onSelectBank}
                allowClear
                refetchOnEmptyValue
                onClear={clearBankData}
              />
            </Form.Item>
          </div>
          <Button
            icon={<PlusOutlined />}
            onClick={() => createBankModalShow()}
            style={{ minWidth: 86, height: 37 }}
          >
            Add
          </Button>
        </div>

        {numberField}
        {ibanField}
        <Form.Item name="accountNumber" rules={accountNumberRules}>
          <Input
            placeholder="Account number"
            style={{
              height: "35px",
            }}
          />
        </Form.Item>
        <Form.Item
          name="currencyCode"
          rules={currencyRulesSimple(currencyData)}
        >
          <AutoComplete
            allowClear
            resource="api/v1/currencies"
            mapItem={mapCurrencyItem}
            onSelect={(_, { data }) => setCurrencyData(data)}
            onClear={clearCurrencyData}
            refetchOnEmptyValue
          >
            <Input placeholder="Transaction currency" size="large" suffix={<DownOutlined />} />
          </AutoComplete>
        </Form.Item>
        <Checkbox
          onChange={onChangeCheckbox}
          defaultChecked={false}
          checked={checkboxState}
        >
          Default company
        </Checkbox>
      </Form>
      <NewBankModalCreate
        closeModal={createBankModalClose}
        formProps={createBankFormProps}
        modalProps={createBankModalProps}
        formIsLoading={createBankFormLoading}
        formSubmit={createBankFormSubmit}
        refetchList={handleBankCreated}
        customerId={details?.id}
      />
    </Modal>
  );
};
